import { useCallback, useEffect, useRef, useState } from "react";
import type {
  StripePaymentElement,
  StripePaymentElementCardDetailsChangeEvent,
} from "@stripe/stripe-js";
import { isBlockedFunding, type CardFunding } from "@/lib/stripe/credit-card";

/**
 * useCardFundingBlock — Detecta en tiempo real el funding de la tarjeta del
 * PaymentElement y bloquea el envío si es prepago (admite débito y crédito).
 *
 * El PaymentElement de react-stripe-js NO expone el evento `carddetailschange`
 * como prop, así que nos suscribimos imperativamente al elemento subyacente
 * en su `onReady`:
 *
 *   const { blocked, register } = useCardFundingBlock(enforcePrepaidBlockOnly);
 *   <PaymentElement onReady={(el) => register(el)} />
 *
 * Stripe.js v8+: `event.details.funding` = "credit" | "debit" | "prepaid" | "unknown".
 * (El evento legacy `change -> event.value.funding` no existe en Stripe.js v8.)
 */
export function useCardFundingBlock(enabled = true) {
  const [blocked, setBlocked] = useState(false);
  const [funding, setFunding] = useState<CardFunding | null | undefined>(undefined);

  const elementRef = useRef<StripePaymentElement | null>(null);
  const handlerRef = useRef<
    ((event: StripePaymentElementCardDetailsChangeEvent) => void) | null
  >(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Los tipos de @stripe/stripe-js declaran `on("carddetailschange", ...)` pero NO el
  // overload de `off` correspondiente (gap de tipos del paquete). El runtime sí soporta
  // quitar el listener; usamos un cast acotado para la limpieza.
  const removeCardDetailsListener = (
    element: StripePaymentElement,
    handler: (event: StripePaymentElementCardDetailsChangeEvent) => void,
  ) => {
    (element as unknown as { off(eventType: string, handler?: unknown): void }).off(
      "carddetailschange",
      handler,
    );
  };

  const clearSubscription = useCallback(() => {
    if (elementRef.current && handlerRef.current) {
      removeCardDetailsListener(elementRef.current, handlerRef.current);
    }
    elementRef.current = null;
    handlerRef.current = null;
  }, []);

  const register = useCallback(
    (element: StripePaymentElement) => {
      clearSubscription();
      if (!enabledRef.current) {
        setFunding(undefined);
        setBlocked(false);
        return;
      }
      elementRef.current = element;
      const handler = (event: StripePaymentElementCardDetailsChangeEvent) => {
        const f = event.details?.funding ?? null;
        setFunding(f);
        setBlocked(isBlockedFunding(f));
      };
      handlerRef.current = handler;
      element.on("carddetailschange", handler);
    },
    [clearSubscription],
  );

  useEffect(() => clearSubscription, [clearSubscription]);

  return { blocked, funding, register };
}